import React, { useState, useEffect, useCallback } from "react";
import QRCode from "react-qr-code";
import api from "./api";
import GuestUpload from "./components/GuestUpload";
import Gallery from "./components/Gallery";
import LiveSlideshow from "./components/LiveSlideshow";
import RSVPForm from "./components/RSVPForm";
import { AnimatePresence } from "framer-motion";

function EventGallery({ eventId, showUpload = true }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [eventData, setEventData] = useState(null);

  const fetchImages = useCallback(async () => {
    try {
      const res = await api.get(`/api/media/event/${eventId}?status=approved`);
      setImages(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const fetchEventData = useCallback(async () => {
    try {
      const res = await api.get(`/api/events/${eventId}`);
      setEventData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [eventId]);

  useEffect(() => {
    fetchImages();
    fetchEventData();
    const interval = setInterval(fetchImages, 15000);
    return () => clearInterval(interval);
  }, [fetchImages, fetchEventData]);

  const scrollToUpload = () => {
    const el = document.getElementById("upload-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  if (eventData && !eventData.isApproved && showUpload) {
    return (
      <div
        className="container py-5 text-center d-flex align-items-center justify-content-center min-vh-100"
        dir="rtl"
      >
        <div className="card shadow-lg border-0 p-5 rounded-celebration bg-white mx-2 maxWidth-600">
          <div className="display-1 text-warning mb-4">⏳</div>
          <h2
            className="fw-black mb-3 text-primary"
            style={{ fontFamily: "var(--font-headline)" }}
          >
            الألبوم ده لسه ما اتفعلش!
          </h2>
          <p className="text-on-surface-variant fs-5 mb-0 arabic-leading">
            صاحب المناسبة لسه بيفعل الألبوم.. ارجع كمان شوية هتلاقي كل اللحظات
            هنا ✨
          </p>
        </div>
      </div>
    );
  }

  const eventUrl = window.location.href;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  return (
    <div className="album-wrapper container py-4 pt-5 px-3 px-md-4">
      <section className="mb-5">
        <div className="bg-surface-container-lowest rounded-celebration p-4 p-md-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary-fixed/30 to-transparent pointer-events-none"></div>
          <div className="relative z-10 flex flex-col md:flex-row-reverse justify-between gap-8 items-center">
            <div className="max-w-2xl w-full flex-grow-1 text-center text-md-start order-2 order-md-1">
              <div className="flex items-center gap-3 mb-4 justify-center justify-content-md-start">
                <span className="bg-secondary-fixed text-on-surface-variant text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-pill">
                  ألبوم الضيوف
                </span>
                <span className="text-on-surface-variant small">
                  {eventData?.eventDate &&
                    new Date(eventData.eventDate).toLocaleDateString("ar-EG", {
                      dateStyle: "long",
                    })}
                </span>
              </div>
              <h1
                className="display-4 fw-black text-on-surface mb-4 tracking-tight"
                style={{ fontFamily: "var(--font-headline)" }}
              >
                {eventData?.name}
              </h1>
              <p className="fs-5 text-on-surface-variant leading-relaxed arabic-leading">
                {eventData?.description ||
                  "شاركنا أجمل لحظات وذكريات اليوم المميز. نجمع هنا كل الصور والفيديوهات التي يشاركها الأهل والأصدقاء."}
              </p>
              <div className="mt-6 d-flex align-items-center gap-4 small text-on-surface-variant justify-center justify-content-md-start">
                {eventData?.locationName && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="material-symbols-outlined text-primary">
                      location_on
                    </span>
                    {eventData.locationGoogleMapsLink ? (
                      <a
                        href={eventData.locationGoogleMapsLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none text-on-surface-variant hover:text-primary fw-bold"
                      >
                        {eventData.locationName}
                      </a>
                    ) : (
                      <span>{eventData.locationName}</span>
                    )}
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <span className="material-symbols-outlined text-primary">
                    photo_library
                  </span>
                  <span>{images.length} مشاركة</span>
                </div>
              </div>
            </div>

            <div className="bg-surface-container-low p-4 rounded-celebration flex flex-column items-center justify-center border border-primary-dim shadow-sm maxWidth-400 mx-auto mx-md-0 shrink-0 flex-grow-1 order-1 order-md-2">
              {eventData?.eventPhoto && (
                <div
                  className="mb-4 w-100 position-relative overflow-hidden rounded-4 border border-primary-dim shadow-sm"
                  style={{ aspectRatio: "1/1" }}
                >
                  <img
                    src={eventData.eventPhoto}
                    alt="Event Blueprint"
                    className="w-100 h-100 object-cover"
                  />
                  <div className="position-absolute bottom-0 start-0 w-100 p-2 bg-gradient-to-t from-black/60 to-transparent text-white text-xs fw-bold text-center">
                    ألبوم {eventData.name}
                  </div>
                </div>
              )}
              <button
                className="btn btn-outline-primary px-4 py-2 rounded-pill fw-bold mb-3 w-100"
                onClick={() => setShowSlideshow(true)}
              >
                📺 العرض الحي
              </button>
              <button
                className="btn btn-primary px-4 py-2 rounded-pill fw-bold w-100 d-flex align-items-center justify-content-center gap-2"
                onClick={copyLink}
              >
                <span className="material-symbols-outlined fs-6">
                  content_copy
                </span>
                نسخ رابط الألبوم
              </button>
            </div>

            <div className="d-none d-md-block bg-white p-3 rounded-4 shadow-sm border border-primary-dim shrink-0 order-3">
              <QRCode value={eventUrl} size={120} />
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {showSlideshow && (
          <LiveSlideshow
            eventId={eventId}
            onClose={() => setShowSlideshow(false)}
          />
        )}
      </AnimatePresence>

      {showUpload && (
        <div className="mb-4 px-2">
          <GuestUpload
            eventId={eventId}
            onUpload={fetchImages}
            eventData={eventData}
          />
        </div>
      )}

      {eventData?.settings?.rsvpEnabled && (eventData?.ownerId?.plan === 'pro' || eventData?.ownerId?.plan === 'corporate') && (
        <div className="mb-4 px-2">
          <RSVPForm eventId={eventId} />
        </div>
      )}

      <div className="album-page px-2">
        {loading ? (
          <div className="bento-grid mt-2" dir="rtl">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="row-span-1">
                <div
                  className="card h-100 shadow-sm border-0 overflow-hidden rounded-celebration"
                  style={{ height: "100%" }}
                >
                  <div className="skeleton-box w-100 h-100 bg-surface-container animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Gallery
            images={images}
            onLike={fetchImages}
            scrollToUpload={scrollToUpload}
            plan={eventData?.ownerId?.plan || 'free'}
          />
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 1.5s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default EventGallery;
